import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import type { GrowerPayment } from '@/lib/api/grower-payment-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { GrowerPaymentFormDialog } from '@/components/grower-payments/grower-payment-form-dialog'
import { GrowerPaymentListCard } from '@/components/grower-payments/grower-payment-list-card'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useGrower } from '@/hooks/use-growers-query'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    useDeleteGrowerPayment,
    useGrowerPaymentSummary,
    useGrowerPayments,
    useGrowerPaymentsInfinite,
} from '@/hooks/use-grower-payment-query'
import { formatDateTimeVN, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { GROWER_PAYMENTS_FILTERS } from '@/lib/schemas/grower-payment-search'

const PER_PAGE = 50

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/grower-payments',
)({
    component: GrowerPaymentsPage,
    // Bộ lọc đẩy lên search param → reload / share link / back giữ nguyên
    // trạng thái đang xem.
    validateSearch: GROWER_PAYMENTS_FILTERS.validateSearch,
})

interface ListProps {
    /** Dữ liệu cho bảng desktop (đã phân trang). */
    items: GrowerPayment[]
    /**
     * Dữ liệu cho card mobile (gom dần qua "Xem thêm"). Bỏ trống thì dùng chung
     * `items` — tiện cho test và cho màn không cần infinite.
     */
    mobileItems?: GrowerPayment[]
    /** Điều khiển nút "Xem thêm" của mobile. */
    loadMore?: {
        hasMore: boolean
        loading: boolean
        onLoadMore: () => void
    }
    onEdit: (row: GrowerPayment) => void
    onDelete: (row: GrowerPayment) => void
    /**
     * Nội dung hiện khi danh sách rỗng. Màn truyền vào để phân biệt "chưa có
     * phiếu" với "không khớp bộ lọc" — chỉ màn mới biết đang lọc gì (R10).
     */
    emptyState?: ReactNode
}

/**
 * Danh sách phiếu: mobile là card (chuẩn theo màn danh sách lô), desktop là
 * bảng. Tách riêng khỏi page để test được mà không cần dựng router.
 */
export function GrowerPaymentsList({
    emptyState,
    items,
    mobileItems,
    loadMore,
    onEdit,
    onDelete,
}: ListProps) {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('grower_payments.write')
    const canDelete = hasPermission('grower_payments.delete')
    const cards = mobileItems ?? items

    if (items.length === 0 && cards.length === 0) {
        return (
            emptyState ?? (
                <p className="text-muted-foreground py-8 text-center text-sm">
                    {m.grower_payment_empty()}
                </p>
            )
        )
    }

    const rowActions = (row: GrowerPayment) => (
        <>
            {canWrite && (
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={m.common_edit()}
                    onClick={() => onEdit(row)}
                >
                    <Pencil className="size-4" />
                </Button>
            )}
            {canDelete && (
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={m.common_delete()}
                    onClick={() => onDelete(row)}
                >
                    <Trash2 className="size-4" />
                </Button>
            )}
        </>
    )

    return (
        <>
            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {cards.map((row) => (
                    <GrowerPaymentListCard
                        key={row.id}
                        payment={row}
                        actions={rowActions(row)}
                        // Chỉ cho tap mở form sửa khi thực sự có quyền sửa.
                        onClick={canWrite ? () => onEdit(row) : undefined}
                    />
                ))}
                {loadMore?.hasMore && (
                    <Button
                        variant="outline"
                        className="h-11 w-full"
                        disabled={loadMore.loading}
                        onClick={loadMore.onLoadMore}
                    >
                        {loadMore.loading
                            ? m.list_loading_more()
                            : m.list_load_more()}
                    </Button>
                )}
            </div>

            {/* Desktop: bảng */}
            <div className="hidden overflow-x-auto md:block">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.grower_payment_grower()}</TableHead>
                            <TableHead className="text-right">
                                {m.grower_payment_amount()}
                            </TableHead>
                            <TableHead>
                                {m.grower_payment_transferred_at()}
                            </TableHead>
                            <TableHead>{m.grower_payment_bill()}</TableHead>
                            <TableHead>
                                {m.grower_payment_col_creator()}
                            </TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <div className="font-medium">
                                        {row.grower_name}
                                    </div>
                                    {row.bank_number && (
                                        <div className="text-muted-foreground text-xs">
                                            {row.bank_number}
                                            {row.bank_name
                                                ? ` · ${row.bank_name}`
                                                : ''}
                                        </div>
                                    )}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                    {formatVnd(row.amount)}
                                </TableCell>
                                <TableCell>
                                    {formatDateTimeVN(row.transferred_at)}
                                </TableCell>
                                <TableCell>
                                    {row.bill_image_url ? (
                                        <a
                                            href={row.bill_image_url}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <img
                                                src={row.bill_image_url}
                                                alt={m.grower_payment_bill()}
                                                className="h-10 w-10 rounded object-cover"
                                            />
                                        </a>
                                    ) : (
                                        <Badge variant="outline">
                                            {m.grower_payment_bill_missing()}
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell>{row.creator_name}</TableCell>
                                <TableCell className="text-right whitespace-nowrap">
                                    {rowActions(row)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </>
    )
}

function GrowerPaymentsPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('grower_payments.write')
    // Tài khoản hộ trồng chỉ có grower_payments.read_own: server đã ép phạm vi
    // về hộ của họ, nên ô lọc theo hộ ở đây vô nghĩa.
    const canReadAll = hasPermission('grower_payments.read')

    const navigate = useNavigate({ from: '/accounting/grower-payments' })
    const urlSearch = Route.useSearch()

    // Chip lọc theo hộ trồng cần TÊN, URL chỉ có id → tra runtime rồi truyền.
    const growerId =
        typeof urlSearch.grower_id === 'string'
            ? urlSearch.grower_id
            : undefined
    const { data: selectedGrower } = useGrower(
        canReadAll ? growerId : undefined,
    )

    const { values, barProps, page, setPage } = useListFilters({
        def: GROWER_PAYMENTS_FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
        // Tài khoản hộ trồng chỉ có `grower_payments.read_own`: server đã ép
        // phạm vi về hộ của họ nên ô lọc theo hộ vô nghĩa, mà ô đó còn cần
        // `growers.read` họ không có.
        hiddenFields: canReadAll ? undefined : ['grower_id'],
        chipLabels: {
            grower_id: selectedGrower
                ? `${m.grower_payment_filter_grower()}: ${selectedGrower.name}`
                : undefined,
        },
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const filters = {
        grower_id: values.grower_id || undefined,
        from_date: values.from_date || undefined,
        to_date: values.to_date || undefined,
    }

    // Desktop phân trang, mobile cuộn dần bằng "Xem thêm" — hai nguồn dữ liệu
    // loại trừ nhau qua `enabled` để không fetch trùng.
    const isMobile = useIsMobile()
    const { data: listRes } = useGrowerPayments(
        { ...filters, page, per_page: PER_PAGE },
        { enabled: !isMobile },
    )
    const infinite = useGrowerPaymentsInfinite(
        { ...filters, per_page: PER_PAGE },
        { enabled: isMobile },
    )
    const mobileItems = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )
    const { data: summary } = useGrowerPaymentSummary(filters)

    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<GrowerPayment | null>(null)
    const [deleting, setDeleting] = useState<GrowerPayment | null>(null)
    const del = useDeleteGrowerPayment()
    const deletionWorkflow = useWorkflowTracking('grower_payment', {
        active: deleting !== null,
        attemptKey: deleting?.id,
        operation: 'delete',
    })

    const items = listRes?.data ?? []
    const meta = listRes?.meta

    return (
        <div className="space-y-4 page-pad">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.grower_payment_page_title()}
                    </h1>
                </div>
                {canWrite && (
                    <Button
                        onClick={() => {
                            setEditing(null)
                            setFormOpen(true)
                        }}
                    >
                        <Plus className="mr-1 size-4" />
                        {m.grower_payment_add()}
                    </Button>
                )}
            </div>

            <ListFilterBar {...barProps} />

            {/* Tổng của cả bộ lọc, không phải của trang đang xem. */}
            <p className="text-sm font-medium">
                {m.grower_payment_total()}:{' '}
                {formatVnd(summary?.total_amount ?? 0)} · {summary?.count ?? 0}{' '}
                {m.grower_payment_count_unit()}
            </p>

            <GrowerPaymentsList
                items={items}
                mobileItems={mobileItems}
                emptyState={
                    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp —
                    // trường hợp thứ hai phải cho lối thoát ngay tại chỗ (R10).
                    barProps.activeCount > 0 ? (
                        <div className="flex flex-col items-center gap-3 py-8 text-center">
                            <span className="text-muted-foreground text-sm">
                                {m.list_empty_filtered()}
                            </span>
                            <ClearFilterButton
                                active
                                onClear={barProps.onClear}
                            />
                        </div>
                    ) : undefined
                }
                loadMore={{
                    hasMore: Boolean(infinite.hasNextPage),
                    loading: infinite.isFetchingNextPage,
                    onLoadMore: () => void infinite.fetchNextPage(),
                }}
                onEdit={(row) => {
                    setEditing(row)
                    setFormOpen(true)
                }}
                onDelete={setDeleting}
            />

            {/* Không có cụm này thì phiếu từ trang 2 trở đi không cách nào xem
                được — dòng tổng vẫn đếm chúng. */}
            {meta && meta.total_pages > 1 && (
                <div className="text-muted-foreground hidden items-center justify-end gap-2 text-sm md:flex">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                    >
                        {m.common_pagination_prev()}
                    </Button>
                    <span className="px-2">
                        {page} / {meta.total_pages}
                    </span>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={page >= meta.total_pages}
                        onClick={() => setPage(page + 1)}
                    >
                        {m.common_pagination_next()}
                    </Button>
                </div>
            )}

            <GrowerPaymentFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                editing={editing}
            />

            <Dialog
                open={Boolean(deleting)}
                onOpenChange={(open) => !open && setDeleting(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.grower_payment_delete_title()}
                        </DialogTitle>
                        <DialogDescription>
                            {m.grower_payment_delete_desc()}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleting(null)}
                        >
                            {m.action_cancel()}
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={del.isPending}
                            onClick={async () => {
                                if (!deleting) return
                                await deletionWorkflow.run(() =>
                                    del.mutateAsync(deleting.id),
                                )
                                setDeleting(null)
                            }}
                        >
                            {m.common_delete()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
